import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useRoutes } from '../../../AppSystem/Routing/useRoutes';
import { LoadLocalStorage } from '../../../Common/Helpers/LoadLocalStorage';
import { CustomReload } from '../../../AppSystem/Helpers/CustomReload';
import { useTranslation } from 'react-i18next';
import { USER_PATHS } from '../../../config';
import LogoLRF from '../../../AppSystem/assets/img/LRF-new-Logga.png';
import LogoLRFWithoutText from '../../../AppSystem/assets/img/LRF-new-logo.png';
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../../../AppSystem/Redux/Auth/AuthSlice";
import { redirectWhenCloseSession } from '../../../Common/Helpers/RedirectWhenCloseSession.js';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import { SaveLocalStorage } from '../../../Common/Helpers/SaveLocalStorage';
import { ButtonChangeLanguage } from "../../../Common/Components/Button/ButtonChangeLanguage";

export function MobileNav() {
  const rawRoutes = useRoutes();
  const location = useLocation();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { tokenData } = useSelector(state => state.auth);
  const { instance, accounts } = useMsal();

  const [panelOpen, setPanelOpen] = useState(false);
  const [forceHidden, setForceHidden] = useState(false);

  useEffect(() => {
    const handler = (e) => setPanelOpen(e.detail.open);
    window.addEventListener('slidePanelChange', handler);
    return () => window.removeEventListener('slidePanelChange', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => setForceHidden(e.detail.hidden);
    window.addEventListener('mobileNavForceHide', handler);
    return () => window.removeEventListener('mobileNavForceHide', handler);
  }, []);

  const routes = useMemo(() => {
    const seen = new Set();
    return rawRoutes.filter(route => {
      const subsKey = route.subs ? route.subs.map(s => s.path).join(',') : '';
      const key = `${route.path ?? ''}-${route.name ?? ''}-${route.dropdown ?? ''}-${subsKey}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawRoutes]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [expandedDropdown, setExpandedDropdown] = useState(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (drawerOpen && drawerRef.current && !drawerRef.current.contains(e.target)) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [drawerOpen]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const changeName = () => {
    localStorage.removeItem('currentPage');
    localStorage.removeItem('itemsPerPage');
  };

  const clearSearchCriteria = (label) => {
    const search = LoadLocalStorage('search');
    if (search) {
      localStorage.removeItem('search');
      const searchCriteria = document.getElementById('searchCriteria');
      if (searchCriteria) {
        const title = LoadLocalStorage('headerTitle');
        if (title && title === label) CustomReload();
      }
    }
  };

  const handleNavClick = (label) => {
    clearSearchCriteria(label);
    changeName();
    if (!window.location.href.includes('internal-transaction')) {
      localStorage.removeItem('originOrgId');
      localStorage.removeItem('message');
    }
    setDrawerOpen(false);
    setExpandedDropdown(null);
  };

  const handleItemClick = (name) => {
    setExpandedDropdown(prev => (prev === name ? null : name));
  };

  const isActive = (path) => location.pathname === path;

  const clearNavStorage = () => {
    if (LoadLocalStorage("search")) localStorage.removeItem('search');
    localStorage.removeItem('currentPage');
    localStorage.removeItem('itemsPerPage');
    if (!window.location.href.includes("internal-transaction")) {
      localStorage.removeItem('originOrgId');
      localStorage.removeItem('message');
    }
  };

  const handleLogout = async () => {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && /Results$/i.test(key)) keysToRemove.push(key);
    }
    keysToRemove.forEach(k => localStorage.removeItem(k));

    if (tokenData) {
      try {
        const decodedToken = jwtDecode(tokenData);
        const loginMethod = decodedToken.loginMethod;
        if (loginMethod && loginMethod.toLowerCase() === 'lrfssoentraid') {
          dispatch(logoutUser());
          const activeAccount = instance.getActiveAccount() || accounts[0];
          if (activeAccount) {
            await instance.logoutRedirect({ account: activeAccount });
          } else {
            await instance.logoutRedirect();
          }
        } else {
          dispatch(logoutUser());
          redirectWhenCloseSession(tokenData);
        }
      } catch {
        dispatch(logoutUser());
        if (tokenData) redirectWhenCloseSession(tokenData);
      }
    } else {
      dispatch(logoutUser());
    }

    SaveLocalStorage('headerTitle', t('dashboard.dashboard'));
    clearNavStorage();
    setDrawerOpen(false);
  };

  return (
    <>
      {drawerOpen && (
        <div onClick={() => setDrawerOpen(false)} className="mobileNavOverlay" />
      )}

      {/* DRAWER */}
      <div
        ref={drawerRef}
        className={`mobileNavDrawer ${drawerOpen ? 'mobileNavDrawerOpen' : 'mobileNavDrawerClosed'}`}
      >
        {/* Cabecera */}
        <div className="mobileNavHeader">
          <Link to={USER_PATHS.APP} onClick={() => handleNavClick(t('dashboard.dashboard'))}>
            <img src={LogoLRF} alt={t('accessibility.logo')} className="mobileNavHeaderLogo" />
          </Link>
          <button onClick={() => setDrawerOpen(false)} className="mobileNavHeaderCloseBtn" aria-label="Cerrar menú">
            <i className="bx bx-x" />
          </button>
        </div>

        {/* Lista rutas */}
        <nav className="mobileNavList">
          <ul className="mobileNavUl">
            {routes.map((route, index) => {
              if (route.hideInMenu === true) return null;

              const titleEl = route.title !== undefined && route.title !== null ? (
                <li key={`mob-title-${route.name}`} className="mobileNavSectionTitle">
                  {route.title}
                </li>
              ) : null;

              if (route.dropdown !== undefined && route.dropdown === true) {
                if (!route.subs || route.subs.length === 0) return null;
                const isOpen = expandedDropdown === route.name;
                return (
                  <React.Fragment key={`mob-drop-${route.name.trim()}-${index}`}>
                    {titleEl}
                    <li>
                      <div
                        role="button"
                        onClick={() => handleItemClick(route.name)}
                        className="mobileNavDropdownBtn"
                      >
                        <i className={`${route.icon} fs-5 mobileNavItemIcon`} />
                        <span style={{ flex: 1 }}>{route.name}</span>
                        <i className={`bx ${isOpen ? 'bx-chevron-up' : 'bx-chevron-down'} mobileNavDropdownChevron`} />
                      </div>
                      <ul className="mobileNavSublist" style={{ maxHeight: isOpen ? 600 : 0 }}>
                        {route.subs.map((item, index2) => (
                          <li key={`${item.label}dropdownOptions${index2}`} className="mobileNavSublistInner">
                            <Link
                              to={item.path}
                              onClick={() => handleNavClick(item.label)}
                              className={`mobileNavSubItem ${isActive(item.path) ? 'mobileNavSubItemActive' : ''}`}
                            >
                              {item.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={`mob-link-${route.name.trim()}-${index}`}>
                  {titleEl}
                  <li>
                    <Link
                      to={route.path}
                      onClick={() => handleNavClick(route.name)}
                      className={`mobileNavItem ${isActive(route.path) ? 'mobileNavItemActive' : ''}`}
                    >
                      <i className={`${route.icon} fs-5 ${isActive(route.path) ? 'mobileNavItemIconActive' : 'mobileNavItemIcon'}`} />
                      <span style={{ flex: 1 }}>{route.name}</span>
                    </Link>
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        </nav>

        {/* Sección inferior */}
        <div className="mobileNavFooter">
          <Link
            to={USER_PATHS.PROFILE}
            onClick={() => {
              SaveLocalStorage('headerTitle', t('modelLabel.profile'));
              clearNavStorage();
              setDrawerOpen(false);
            }}
            className="mobileNavFooterItem"
          >
            <i className="bx bx-user-circle mobileNavFooterIcon" />
            <span>{t('modelLabel.profile')}</span>
          </Link>

          <Link
            to={USER_PATHS.SETTINGS}
            onClick={() => {
              SaveLocalStorage('headerTitle', t('userSetting.settings'));
              clearNavStorage();
              setDrawerOpen(false);
            }}
            className="mobileNavFooterItem"
          >
            <i className="bx bx-id-card mobileNavFooterIcon" />
            <span>{t('userSetting.settings')}</span>
          </Link>

          <ButtonChangeLanguage type="backend" variant="sidebar" />

          <div className="mobileNavFooterDivider" />

          <button onClick={handleLogout} className="mobileNavFooterLogoutBtn">
            <i className="bx bx-log-out mobileNavFooterIcon" />
            <span>{t('modelLabel.logOut')}</span>
          </button>
        </div>
      </div>

      {/* Bottom Tab Bar */}
      <nav className={`mobileNavTabBar ${(panelOpen || forceHidden) ? 'mobileNavTabBarHidden' : ''}`}>
        <button
          onClick={() => setDrawerOpen(true)}
          className="mobileNavTabBtn"
          aria-label="Abrir menú"
        >
          <i className="bx bx-menu" style={{ fontSize: 24 }} />
          <span>{t('menu.menu', 'Meny')}</span>
        </button>

        <Link
          to={USER_PATHS.APP}
          onClick={() => handleNavClick(t('dashboard.dashboard'))}
          className={`mobileNavTabBtn ${isActive(USER_PATHS.APP) ? 'mobileNavTabBtnActive' : ''}`}
          style={{ textDecoration: 'none' }}
        >
          <div style={{
            width: 35, height: 28,
            borderRadius: '40%',
            background: '#cde0da',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 4,
          }}>
            <img src={LogoLRFWithoutText} alt="Logo LRF" className="mobileNavTabLogo" />
          </div>
          <span>{t('menu.startsida', 'Startsida')}</span>
        </Link>

        <button disabled className="mobileNavTabBtnDisabled">
          <i className="bx bx-help-circle" style={{ fontSize: 24 }} />
          <span>{t('menu.guides', 'Guider')}</span>
        </button>
      </nav>

      <div className={`mobileNavSpacer ${(panelOpen || forceHidden) ? 'mobileNavSpacerHidden' : ''}`} />
    </>
  );
}